/**
 * registration-error-text.ts - the api's refusal codes of the Brokermandat registration in words:
 * the Meldung decisions (S4), the insurer-answer decisions, the review task and the per-policy
 * exception (S5), the Beleg check and the Zuordnen task (S5 review round 1), the Beleg of a lifted exception (round 2). Anything else gets the general sentence.
 */

const ERROR_TEXT: Record<string, string> = {
  registration_no_address: 'Für diesen Versicherer ist keine Meldeadresse gepflegt. Bitte zuerst die Benachrichtigungsadresse beim Versicherer erfassen.',
  registration_carrier_unknown: 'Der Versicherer ist in Brokly noch nicht erfasst. Bitte zuerst den Versicherer anlegen.',
  registration_already_released: 'Diese Meldung wurde bereits freigegeben oder wird gerade versendet.',
  registration_not_releasable: 'Diese Meldung kann in ihrem jetzigen Status nicht freigegeben werden.',
  registration_no_mailbox: 'Kein Postfach verbunden: weder ein eigenes noch das gemeinsame Postfach.',
  registration_shared_mailbox_not_member:
    'Ihr eigenes Postfach ist nicht verbunden, und Sie sind nicht Mitglied des gemeinsamen Postfachs. Bitte das eigene Postfach verbinden oder die Mitgliedschaft im gemeinsamen Postfach anfragen.',
  registration_nothing_to_report: 'Bei diesem Versicherer gibt es nichts zu melden.',
  registration_live_exists: 'Für diesen Versicherer läuft bereits eine Meldung.',
  termination_open: 'Das Mandat wird aufgelöst: Meldungen zur Eintragung sind nicht mehr möglich.',
  termination_live: 'Für diesen Kunden läuft eine Mandatsauflösung.',
  mandate_not_signed: 'Das Mandat ist nicht (mehr) unterzeichnet.',
  termination_not_running: 'Die Mandatsauflösung ist nicht (mehr) wirksam: die Auflösungsmeldung ruht.',
  not_supported_for_prozess: 'Diese Aktion gibt es für eine Auflösungsmeldung nicht.',
  registration_not_closable: 'Diese Meldung kann nicht mehr geschlossen werden.',
  registration_record_failed: 'Die E-Mail wurde versendet, konnte aber nicht verbucht werden. Die Meldung steht als «Versand unklar» zur Prüfung.',
  registration_send_failed: 'Die E-Mail konnte nicht versendet werden. Die Meldung wartet wieder auf die Freigabe.',
  registration_send_check_failed:
    'Brokly konnte im Postfach nicht prüfen, ob die Meldung schon versendet wurde. Es wurde nichts gesendet; bitte später erneut freigeben.',
  registration_send_check_reauth:
    'Das Postfach muss neu verbunden werden, damit Brokly prüfen kann, ob die Meldung schon versendet wurde. Danach erneut freigeben.',
  registration_send_found_unrecorded:
    'Die Meldung wurde bereits versendet (im Postfach gefunden), konnte aber nicht automatisch verbucht werden. Es wurde nichts erneut gesendet; die Meldung steht weiter als «Versand unklar» zur Prüfung.',
  no_document: 'Das unterzeichnete Mandat ist nicht lesbar.',
  mailbox_not_connected: 'Das Postfach ist nicht verbunden.',
  permission_denied: 'Dafür fehlt die Berechtigung.',
  // The insurer's answer (Bestätigen, Ablehnen, Rückfrage, Korrektur) and the review task.
  beleg_required: 'Dafür braucht es einen Beleg: bitte ein Dokument oder eine E-Mail angeben.',
  illegal_transition: 'Diese Entscheidung ist im jetzigen Status der Meldung nicht möglich. Bitte die Seite aktualisieren.',
  registration_superseded: 'Für diesen Versicherer gibt es bereits eine neuere Meldung. Die Korrektur gehört dorthin.',
  effective_date_implausible: 'Das Wirkungsdatum ist unplausibel (zu weit vor oder nach der Unterzeichnung des Mandats).',
  effective_date_required: 'Bitte ein Wirkungsdatum angeben.',
  proposal_already_decided: 'Diese Antwort des Versicherers wurde bereits entschieden.',
  proposal_not_found: 'Zu dieser Aufgabe gibt es keine Antwort des Versicherers.',
  policy_not_found: 'Die Police wurde nicht gefunden.',
  // Review round 1: the Beleg check, the Zuordnen task, the lifted exception and the courtage window.
  beleg_not_found: 'Der Beleg gehört nicht zu diesem Kunden oder dieser Meldung.',
  reason_required: 'Bitte einen Grund wählen.',
  notiz_required: 'Bitte festhalten, was der Versicherer geantwortet hat.',
  registration_not_candidate: 'Diese Meldung kommt für diese E-Mail nicht in Frage. Bitte eine der vorgeschlagenen Meldungen wählen.',
  zuordnen_task_not_found: 'Diese Aufgabe ist keine offene Zuordnung (mehr). Bitte die Seite aktualisieren.',
  registration_not_found: 'Die Meldung wurde nicht gefunden.',
  outcome_required: 'Bitte das Ergebnis wählen: eingetragen oder abgelehnt.',
  invalid_courtage_window:
    'Das Wirkungsdatum liegt nach dem Ende der Courtageberechtigung einer Police. Bitte das Datum prüfen oder die Police ausnehmen.',
  // Review round 2: lifting an exception needs a Beleg of the policy's registration.
  policy_not_registered: 'Diese Police ist keiner Meldung zugeordnet.',
  // The reminder loop: «Jetzt erinnern» and «Eskalieren».
  registration_already_reminded: 'Der Versicherer wurde bereits erinnert. Eine weitere Erinnerung ist nicht vorgesehen.',
  registration_not_open:
    'Diese Meldung ist nicht mehr offen, oder das Mandat ist beendet beziehungsweise der Prozess abgeschlossen. Bitte die Seite aktualisieren.',
  registration_already_escalated: 'Für diese Meldung ist bereits eine Klärungsaufgabe offen.',
  registration_no_reminder_configured: 'Für diesen Prozess sind keine Erinnerungen an den Versicherer eingestellt (Versicherer-Fristen).',
  app_user_not_found: 'Ihr Benutzer ist in Brokly nicht vollständig eingerichtet. Bitte die Administration kontaktieren.',
  registration_reminder_in_progress: 'Eine Erinnerung an diesen Versicherer wird gerade versendet. Bitte warten und die Seite aktualisieren.',
  registration_reminder_not_unclear: 'Der Versand dieser Erinnerung ist nicht mehr offen. Bitte die Seite aktualisieren.',
  altbestand_task_not_found: 'Zu dieser Aufgabe gibt es keinen Altbestand (mehr).',
};

/** The refusal of a registration decision in words. */
export function registrationErrorText(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return (typeof code === 'string' && ERROR_TEXT[code]) || 'Die Aktion konnte nicht ausgeführt werden.';
}

/** Why a parsed reply went to a human (the review task), in words. */
export const REGISTRATION_REVIEW_REASON_TEXT: Record<string, string> = {
  effective_date_implausible: 'Wirkungsdatum unplausibel',
  unmatched_exclusion_text: 'Ausgenommene Police nicht eindeutig erkannt',
  match_changed: 'Zuordnung hat sich während der Auswertung geändert',
  apply_failed: 'Automatische Übernahme nicht möglich',
  sender_not_carrier: 'Absender ist nicht der Versicherer',
  manuell_zugeordnet: 'Manuell zugeordnet',
  sender_unverified: 'Absender nicht verifiziert: die Mail ist nicht authentifiziert und wird nie automatisch übernommen.',
};

export function reviewReasonText(code: string): string {
  return REGISTRATION_REVIEW_REASON_TEXT[code] ?? code;
}
